import React, {ReactNode, useEffect, useRef} from 'react';
import {apiClient} from '@common/http/query-client';
import {ChannelContentModel} from '@app/admin/channels/channel-content-config';
import {ALBUM_MODEL} from '@app/web-player/albums/album';
import {ARTIST_MODEL} from '@app/web-player/artists/artist';
import {RADIO_MODEL} from '@app/web-player/radio/radio';
import {TRACK_MODEL} from '@app/web-player/tracks/track';
import {useAuth} from '@common/auth/use-auth';

const MIN_VISIBLE_DURATION_MS = 1000;
const FLUSH_DELAY_MS = 600;
const MAX_RETRY_ATTEMPTS = 3;
const FULLY_VISIBLE_THRESHOLD = 0.999;
const REPEAT_WINDOW_MS = 15 * 1000;

const TRACKABLE_MODEL_TYPES = new Set([
  TRACK_MODEL,
  ALBUM_MODEL,
  ARTIST_MODEL,
  RADIO_MODEL,
]);

type ImpressionPayload = {
  content_id: number;
  content_type: string;
  visible_ms: number;
};

const pendingByChannel = new Map<number, Map<string, ImpressionPayload>>();
const inFlightKeys = new Set<string>();
const trackedAtByKey = new Map<string, number>();
const retryCountByKey = new Map<string, number>();
let flushTimeoutId: number | null = null;

function makeImpressionKey(
  channelId: number,
  contentType: string,
  contentId: number,
): string {
  return `${channelId}:${contentType}:${contentId}`;
}

function scheduleFlush(): void {
  if (flushTimeoutId != null || typeof window === 'undefined') {
    return;
  }

  flushTimeoutId = window.setTimeout(() => {
    flushTimeoutId = null;
    void flushImpressions();
  }, FLUSH_DELAY_MS);
}

function wasTrackedRecently(key: string): boolean {
  const trackedAt = trackedAtByKey.get(key);
  if (!trackedAt) {
    return false;
  }

  if (Date.now() - trackedAt > REPEAT_WINDOW_MS) {
    trackedAtByKey.delete(key);
    return false;
  }

  return true;
}

async function flushImpressions(): Promise<void> {
  const snapshots = [...pendingByChannel.entries()].map(
    ([channelId, impressionsMap]) => ({
      channelId,
      impressions: [...impressionsMap.entries()].map(([key, payload]) => ({
        key,
        payload,
      })),
    }),
  );

  pendingByChannel.clear();

  await Promise.allSettled(
    snapshots.map(async ({channelId, impressions}) => {
      if (!impressions.length) {
        return;
      }

      impressions.forEach(({key}) => inFlightKeys.add(key));

      try {
        await apiClient.post(`channel/${channelId}/impressions`, {
          impressions: impressions.map(({payload}) => payload),
        });

        impressions.forEach(({key}) => {
          inFlightKeys.delete(key);
          trackedAtByKey.set(key, Date.now());
          retryCountByKey.delete(key);
        });
      } catch {
        impressions.forEach(({key, payload}) => {
          inFlightKeys.delete(key);
          const nextRetryCount = (retryCountByKey.get(key) || 0) + 1;
          retryCountByKey.set(key, nextRetryCount);

          if (nextRetryCount >= MAX_RETRY_ATTEMPTS) {
            return;
          }

          let channelPending = pendingByChannel.get(channelId);
          if (!channelPending) {
            channelPending = new Map();
            pendingByChannel.set(channelId, channelPending);
          }

          channelPending.set(key, payload);
        });
      }
    }),
  );

  if (pendingByChannel.size) {
    scheduleFlush();
  }
}

function queueImpression(
  channelId: number,
  contentType: string,
  contentId: number,
): void {
  const key = makeImpressionKey(channelId, contentType, contentId);
  if (wasTrackedRecently(key) || inFlightKeys.has(key)) {
    return;
  }

  let channelPending = pendingByChannel.get(channelId);
  if (!channelPending) {
    channelPending = new Map();
    pendingByChannel.set(channelId, channelPending);
  }

  if (channelPending.has(key)) {
    return;
  }

  channelPending.set(key, {
    content_id: contentId,
    content_type: contentType,
    visible_ms: MIN_VISIBLE_DURATION_MS,
  });

  scheduleFlush();
}

interface Props {
  channelId: number;
  item: ChannelContentModel;
  children: ReactNode;
}

export function ChannelImpressionTracker({channelId, item, children}: Props) {
  const {isLoggedIn} = useAuth();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isFullyVisibleRef = useRef(false);
  const hasTrackedRef = useRef(false);
  const visibleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    if (
      typeof item.model_type !== 'string' ||
      !TRACKABLE_MODEL_TYPES.has(item.model_type)
    ) {
      return;
    }

    const contentType = item.model_type;
    const contentId = Number(item.id);
    if (!Number.isFinite(contentId) || contentId <= 0 || channelId <= 0) {
      return;
    }

    const element = wrapperRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const clearVisibleTimer = () => {
      if (visibleTimerRef.current != null) {
        window.clearTimeout(visibleTimerRef.current);
        visibleTimerRef.current = null;
      }
    };

    let observer: IntersectionObserver | null = null;

    const maybeStartTimer = () => {
      if (hasTrackedRef.current || visibleTimerRef.current != null) {
        return;
      }

      if (
        !isFullyVisibleRef.current ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      visibleTimerRef.current = window.setTimeout(() => {
        visibleTimerRef.current = null;

        if (
          hasTrackedRef.current ||
          !isFullyVisibleRef.current ||
          document.visibilityState !== 'visible'
        ) {
          return;
        }

        hasTrackedRef.current = true;
        queueImpression(channelId, contentType, contentId);
        observer?.unobserve(element);
      }, MIN_VISIBLE_DURATION_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearVisibleTimer();
      } else {
        maybeStartTimer();
      }
    };

    observer = new IntersectionObserver(
      ([entry]) => {
        isFullyVisibleRef.current =
          entry.isIntersecting &&
          entry.intersectionRatio >= FULLY_VISIBLE_THRESHOLD;

        if (isFullyVisibleRef.current) {
          maybeStartTimer();
        } else {
          clearVisibleTimer();
        }
      },
      {
        threshold: [FULLY_VISIBLE_THRESHOLD, 1],
      },
    );

    observer.observe(element);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearVisibleTimer();
      observer?.unobserve(element);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [channelId, isLoggedIn, item.id, item.model_type]);

  return <div ref={wrapperRef}>{children}</div>;
}
